import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { enqueue, generateClientRequestId, type MutationOp } from './offlineQueue';
import { flushOfflineQueue } from './offlineFlush';
import { logWarn } from './logger';

/**
 * Schema-agnostic handle to the client. These helpers mutate table names only
 * known at runtime, so the generated per-table typing cannot apply — the
 * untyped default client is the intended seam.
 */
const db = supabase as unknown as SupabaseClient;

/**
 * Helper unique pour les mutations qui doivent survivre offline.
 *
 * Stratégie :
 *   1. Génère un `client_request_id` UUID
 *   2. Tente d'exécuter immédiatement contre Supabase
 *   3. Si network error OU offline → enqueue + retourne `{ queued: true }`
 *   4. Si succès → flush la queue en arrière-plan (au cas où d'autres mutations attendent)
 *
 * Le caller fait l'optimistic UI en local AVANT d'appeler ce helper. Cf.
 * notesStore.createNote pour le pattern.
 */

export type OfflineMutationResult<T> =
  | { ok: true; data: T; queued: false }
  | { ok: true; data: null; queued: true; clientRequestId: string }
  | { ok: false; error: string };

interface InsertParams {
  table: string;
  payload: Record<string, unknown>;
  select?: string;
}

export async function insertWithOfflineFallback<T>(
  params: InsertParams,
): Promise<OfflineMutationResult<T>> {
  const clientRequestId = generateClientRequestId();
  const enriched = { ...params.payload, client_request_id: clientRequestId };

  if (!navigator.onLine) {
    // Offline : queue direct, on ne tente pas le réseau
    await enqueue({ id: clientRequestId, table: params.table, op: 'insert', payload: enriched });
    return { ok: true, data: null, queued: true, clientRequestId };
  }

  try {
    const q = params.select
      ? db.from(params.table).insert(enriched).select(params.select).single()
      : db.from(params.table).insert(enriched).select().single();
    const { data, error } = await q;
    if (error) {
      // Erreur 5xx ou network-like → queue ; sinon → return l'erreur métier
      if (isRetryableError(error)) {
        await enqueue({
          id: clientRequestId,
          table: params.table,
          op: 'insert',
          payload: enriched,
        });
        logWarn('offline', 'insert queued after retryable error', {
          table: params.table,
          error: error.message,
        });
        return { ok: true, data: null, queued: true, clientRequestId };
      }
      return { ok: false, error: error.message };
    }
    void flushOfflineQueue(); // best-effort, drainage en arrière-plan
    return { ok: true, data: data as T, queued: false };
  } catch (err) {
    // Network failure (fetch throw)
    await enqueue({
      id: clientRequestId,
      table: params.table,
      op: 'insert',
      payload: enriched,
    });
    logWarn('offline', 'insert queued after fetch throw', {
      table: params.table,
      error: (err as Error).message,
    });
    return { ok: true, data: null, queued: true, clientRequestId };
  }
}

interface UpdateParams {
  table: string;
  payload: Record<string, unknown>;
  match: Record<string, unknown>;
}

export async function updateWithOfflineFallback(
  params: UpdateParams,
): Promise<OfflineMutationResult<void>> {
  const clientRequestId = generateClientRequestId();

  if (!navigator.onLine) {
    await enqueue({
      id: clientRequestId,
      table: params.table,
      op: 'update',
      payload: params.payload,
      match: params.match,
    });
    return { ok: true, data: null, queued: true, clientRequestId };
  }

  try {
    let q = db.from(params.table).update(params.payload);
    for (const [k, v] of Object.entries(params.match)) {
      q = q.eq(k, v as string | number | boolean);
    }
    const { error } = await q;
    if (error) {
      if (isRetryableError(error)) {
        await enqueue({
          id: clientRequestId,
          table: params.table,
          op: 'update',
          payload: params.payload,
          match: params.match,
        });
        return { ok: true, data: null, queued: true, clientRequestId };
      }
      return { ok: false, error: error.message };
    }
    void flushOfflineQueue();
    return { ok: true, data: undefined as unknown as void, queued: false };
  } catch (err) {
    await enqueue({
      id: clientRequestId,
      table: params.table,
      op: 'update',
      payload: params.payload,
      match: params.match,
    });
    logWarn('offline', 'update queued after fetch throw', {
      table: params.table,
      error: (err as Error).message,
    });
    return { ok: true, data: null, queued: true, clientRequestId };
  }
}

function isRetryableError(error: { code?: string; message?: string }): boolean {
  const code = error.code ?? '';
  // 5xx + erreurs réseau-like = retry
  if (code.startsWith('5')) return true;
  if (/network|fetch|timeout/i.test(error.message ?? '')) return true;
  return false;
}

// Re-export op type for callers
export type { MutationOp };
